import { useEffect, useMemo, useState } from 'react';
import { GameModeCards } from './GameModeCards.jsx';

const GAMEPLAY_HASHES = new Set(['street', 'market', 'travel', 'stash', 'more']);

export function AuthPortal({
  hubState,
  hubMessage,
  config,
  registration,
  gameModes,
  selectedGameModeId,
  onGameModeSelect,
  busy,
  loadingSavedRun,
  onLogin,
  onRegister,
  onPlayOffline,
}) {
  const inviteBootstrap = useMemo(() => {
    if (typeof window === 'undefined') return { register: false, inviteCode: '' };
    const params = new URLSearchParams(window.location.search);
    return {
      register: window.location.pathname.replace(/\/+$/, '') === '/register' || params.has('invite'),
      inviteCode: params.get('invite') ?? '',
    };
  }, []);
  const [mode, setMode] = useState(inviteBootstrap.register ? 'register' : 'login');
  const [form, setForm] = useState({
    email: '',
    alias: '',
    password: '',
    confirmation: '',
    inviteCode: inviteBootstrap.inviteCode,
  });
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const staleView = window.location.hash.replace(/^#/, '').toLowerCase();
    if (GAMEPLAY_HASHES.has(staleView)) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
    const resetScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    resetScroll();
    const frame = window.requestAnimationFrame(resetScroll);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const update = (key) => (event) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const changeMode = (nextMode) => {
    setMode(nextMode);
    setLocalError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    setLocalError('');
    if (mode === 'register') {
      if (form.password !== form.confirmation) {
        setLocalError('Passphrases do not match.');
        return;
      }
      if (form.password.length < 12) {
        setLocalError('Passphrase must be at least 12 characters.');
        return;
      }
      if (registration.invite_required && !form.inviteCode.trim()) {
        setLocalError('An invitation code is required to register.');
        return;
      }
      await onRegister({
        email: form.email,
        alias: form.alias,
        password: form.password,
        inviteCode: form.inviteCode,
        gameModeId: selectedGameModeId,
      });
      return;
    }
    await onLogin({ email: form.email, password: form.password });
  };

  const registrationClosed = registration.mode === 'closed';
  const stateClass = hubState.toLowerCase().replace(/\s+/g, '-');

  return (
    <main className="login-shell v2-auth-shell">
      <section className="auth-card v2-auth-card" aria-labelledby="auth-game-title">
        <header className="auth-brand v2-auth-brand">
          <img className="brand-logo login-logo" src="./branding/the-dope-man-logo.webp" alt="The Dope Man" />
          <div className="auth-brand-copy">
            <div className="eyebrow">THE DOPE MAN · ONLINE</div>
            <h1 id="auth-game-title">THE DOPE MAN</h1>
            <p>Your account is the front door. The hub remains authoritative for sessions, saves, run rules, and registration.</p>
          </div>
          <div className={`hub-badge ${stateClass}`} aria-label={`Server status: ${hubState}`}>{hubState}</div>
        </header>

        <div className="login-panel v2-auth-panel" aria-labelledby="identity-title">
          <div className="login-panel-topline">
            <div>
              <div className="eyebrow">PLAYER ACCOUNT</div>
              <h2 id="identity-title">{mode === 'login' ? 'Sign in' : 'Create a player'}</h2>
            </div>
          </div>

          {loadingSavedRun ? (
            <div className="auth-loading" aria-live="polite">
              <span className="bootstrap-spinner" aria-hidden="true" />
              <p className="login-copy">Restoring your saved run…</p>
            </div>
          ) : (
            <>
              <div className="auth-tabs" role="tablist" aria-label="Player account access">
                <button className={`auth-tab ${mode === 'login' ? 'active' : ''}`} type="button" role="tab" aria-selected={mode === 'login'} onClick={() => changeMode('login')}>Sign In</button>
                <button className={`auth-tab ${mode === 'register' ? 'active' : ''}`} type="button" role="tab" aria-selected={mode === 'register'} disabled={registrationClosed} onClick={() => changeMode('register')}>Register</button>
              </div>

              <p className="login-copy">
                {mode === 'login'
                  ? 'Use the email and passphrase attached to your player account.'
                  : 'Create a secure account with your invitation code and street alias.'}
              </p>

              <form onSubmit={submit} className="login-form">
                <label>Email<input type="email" autoComplete="email" inputMode="email" required value={form.email} onChange={update('email')} disabled={busy} /></label>

                {mode === 'register' && (
                  <label>Street alias<input type="text" minLength="3" maxLength="24" pattern="[A-Za-z0-9_-]+" autoComplete="nickname" required value={form.alias} onChange={update('alias')} disabled={busy} /></label>
                )}

                <label>Passphrase<input type="password" minLength={mode === 'register' ? 12 : 1} maxLength="128" autoComplete={mode === 'register' ? 'new-password' : 'current-password'} required value={form.password} onChange={update('password')} disabled={busy} /></label>

                {mode === 'register' && (
                  <>
                    <label>Confirm passphrase<input type="password" minLength="12" maxLength="128" autoComplete="new-password" required value={form.confirmation} onChange={update('confirmation')} disabled={busy} /></label>
                    {registration.invite_required && (
                      <label>Invitation / authorization code<input type="text" maxLength="128" autoComplete="one-time-code" required value={form.inviteCode} onChange={update('inviteCode')} disabled={busy} /></label>
                    )}
                    <section className="auth-path-selector" aria-labelledby="auth-path-title">
                      <div className="eyebrow">CHOOSE YOUR PATH</div>
                      <h3 id="auth-path-title">Preferred first run</h3>
                      <GameModeCards modes={gameModes} selectedModeId={selectedGameModeId} onSelect={onGameModeSelect} disabled={busy} compact legend="Choose your path" />
                      <p className="fine-print auth-mode-note">This is only a preference. The server shows and verifies the complete run preview before creating a save.</p>
                    </section>
                  </>
                )}

                {localError && <div className="form-error" role="alert">{localError}</div>}
                {registrationClosed && mode === 'register' && <div className="form-error">New player registration is temporarily closed.</div>}

                <button className="btn buy primary-action auth-submit" type="submit" disabled={busy || hubState === 'OFFLINE' || (mode === 'register' && registrationClosed)}>
                  {busy ? 'Working…' : mode === 'login' ? 'Enter the Game' : 'Create Player'}
                </button>
              </form>

              <div className="auth-secondary">
                <div className="registration-state">Registration: <strong>{registration.mode.toUpperCase()}</strong>{registration.invite_required ? ' · code required' : ''}</div>
                <button className="auth-offline-link" type="button" onClick={onPlayOffline}>Play Offline Classic</button>
              </div>
            </>
          )}

          <div className="hub-message" aria-live="polite">{hubMessage}</div>
          <div className="build-line">Client {config.appVersion} · {config.runtime}</div>
        </div>
      </section>
    </main>
  );
}
